import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Paper,
  Slider,
  TextField,
  Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorIcon from '@mui/icons-material/ErrorOutline'
import GroupIcon from '@mui/icons-material/GroupOutlined'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import WarningIcon from '@mui/icons-material/WarningAmberOutlined'
import {
  DESCRIPTIONS_SEGMENT,
  LIBELLES_SEGMENT,
  LIBELLES_STATUT_CAMPAGNE,
  campagneModifiable,
  transitionPossible,
  OPTIONS_PAR_DEFAUT,
  TOKENS_MESSAGE,
  TYPES_SEGMENT,
  campagneInputSchema,
  controlerMessage,
  segmentSchema,
  type Campagne,
  type EstimationSegment,
  type OptionsCampagne,
  type Segment,
  type TypeSegment,
} from '@iobiz/shared'
import { ApiError, api } from '../../api/client.js'
import { ChampDate } from '../../ui/ChampDate.js'
import { ChoixPortrait } from '../../ui/ChoixPortrait.js'
import { AssistantIA } from './AssistantIA.js'
import { ChoixDestinataires } from './ChoixDestinataires.js'

/**
 * Création et modification d'une campagne.
 *
 * ⭐ Une seule fenêtre, quatre blocs dans l'ordre où l'on pense : à qui je
 * parle, ce que je dis, quand je l'envoie, comment. Un assistant en colonne de
 * droite, ouvert en permanence. Un assistant en plusieurs étapes aurait
 * empêché de revenir corriger le message après avoir vu le nombre de
 * destinataires — qui est précisément ce qui fait changer le message.
 */

const HEURES = Array.from({ length: 24 }, (_, h) => `${String(h).padStart(2, '0')}:00`)

/** ISO local → « AAAA-MM-JJ » et « HH:mm », en composantes LOCALES. */
function decouper(iso: string | null): { jour: string; heure: string } {
  if (!iso) return { jour: '', heure: '10:00' }
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return { jour: '', heure: '10:00' }
  const p = (n: number): string => String(n).padStart(2, '0')
  return {
    jour: `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`,
    heure: `${p(d.getHours())}:${p(d.getMinutes())}`,
  }
}

/**
 * ⚠️ Reconstruit l'instant depuis les composantes LOCALES, jamais en collant
 * les chaînes : « 2026-09-10T14:00 » interprété en UTC ferait partir la
 * campagne à 15 h à Paris et à 15 h aussi à Douala, ce qui ne veut rien dire.
 */
function recomposer(jour: string, heure: string): string | null {
  if (!jour) return null
  const [a, m, j] = jour.split('-').map(Number)
  const [h, mi] = heure.split(':').map(Number)
  if (!a || !m || !j) return null
  return new Date(a, m - 1, j, h ?? 0, mi ?? 0, 0, 0).toISOString()
}

function aujourdhui(): string {
  const d = new Date()
  const p = (n: number): string => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

export function CampagneDialog({
  storeId,
  campagne,
  onFermer,
  onEnregistre,
  onRapport,
  onAction,
  onRejouer,
}: {
  storeId: string
  /** Absent = création. */
  campagne?: Campagne | null
  onFermer: () => void
  onEnregistre: (c: Campagne) => void
  /** Ouvre le rapport — accessible depuis la gestion, à tout moment. */
  onRapport?: (id: string) => void
  /** Lancer, suspendre, reprendre… confiés à la page, qui rafraîchit la liste. */
  onAction?: (c: Campagne, action: string, libelle: string) => void
  onRejouer?: (c: Campagne) => void
}): JSX.Element {
  const [nom, setNom] = useState(campagne?.name ?? '')
  const [objet, setObjet] = useState(campagne?.internalSubject ?? '')
  const [message, setMessage] = useState(campagne?.message ?? '')
  const [segment, setSegment] = useState<Segment>(campagne?.segment ?? { type: 'all' })
  const [selection, setSelection] = useState<string[] | null>(campagne?.selection ?? null)
  const [mediaUrl, setMediaUrl] = useState(campagne?.mediaUrl ?? '')
  const [options, setOptions] = useState<OptionsCampagne>(campagne?.options ?? OPTIONS_PAR_DEFAUT)
  const depart = decouper(campagne?.scheduledAt ?? null)
  const [jour, setJour] = useState(depart.jour)
  const [heure, setHeure] = useState(depart.heure)
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)

  const [estimation, setEstimation] = useState<EstimationSegment | null>(null)
  const [estimationEnCours, setEstimationEnCours] = useState(false)

  const defauts = useMemo(() => controlerMessage(message), [message])
  const bloquants = defauts.filter((d) => d.gravite === 'bloquant')

  /**
   * ⭐ Une campagne partie ne se modifie plus, mais elle se GÈRE : c'est la
   * même fenêtre, en lecture seule, avec les commandes de pilotage au pied.
   * Ouvrir le rapport au clic sur la ligne — comme c'était le cas — répondait
   * à « qu'est-ce que ça a donné » quand la question était « qu'est-ce que
   * c'est, et qu'est-ce que j'en fais ».
   */
  const modifiable = !campagne || campagneModifiable(campagne.status)

  /**
   * L'estimation suit le segment, avec un anti-rebond : chaque frappe dans
   * « ville » ne doit pas déclencher un balayage complet du fichier clients.
   */
  const estimer = useCallback(async (s: Segment, o: OptionsCampagne): Promise<void> => {
    setEstimationEnCours(true)
    try {
      const r = await api.post<EstimationSegment>(`/stores/${storeId}/campaigns/estimation`, {
        segment: s,
        options: o,
      })
      setEstimation(r)
    } catch {
      // Une estimation indisponible n'empêche pas de créer la campagne : la
      // liste sera figée à la préparation, et c'est elle qui fait foi.
      setEstimation(null)
    } finally {
      setEstimationEnCours(false)
    }
  }, [storeId])

  useEffect(() => {
    const t = setTimeout(() => {
      const parsed = segmentSchema.safeParse(segment)
      if (parsed.success) void estimer(parsed.data, options)
    }, 400)
    return () => clearTimeout(t)
  }, [segment, options, estimer])

  function changerType(type: TypeSegment): void {
    // Chaque type repart de ses valeurs par défaut : conserver « ville = Douala »
    // en passant à « Tous les clients » aurait laissé un critère invisible.
    const parsed = segmentSchema.safeParse(
      type === 'active'
        ? { type, joursActivite: 90 }
        : type === 'inactive'
          ? { type, joursInactivite: 180 }
          : type === 'vip'
            ? { type, etiquette: 'VIP' }
            : { type },
    )
    if (parsed.success) {
      setSegment(parsed.data)
      // ⚠️ La sélection nominative porte sur l'ANCIEN segment : la garder
      // laisserait des identifiants qui n'en font plus partie, et le décompte
      // afficherait des destinataires que le gel écarterait ensuite.
      setSelection(null)
    }
  }

  function insererToken(token: string): void {
    setMessage((m) => `${m}${m && !m.endsWith(' ') ? ' ' : ''}${token}`)
  }

  async function enregistrer(): Promise<void> {
    setErreur(null)
    const parsed = campagneInputSchema.safeParse({
      name: nom,
      internalSubject: objet || undefined,
      message,
      segment,
      selection,
      mediaUrl: mediaUrl.trim() || null,
      scheduledAt: recomposer(jour, heure),
      options,
    })
    if (!parsed.success) {
      setErreur(parsed.error.issues[0]?.message ?? 'Saisie invalide')
      return
    }

    setOccupe(true)
    try {
      const enregistree = campagne
        ? await api.patch<Campagne>(`/stores/${storeId}/campaigns/${campagne.id}`, parsed.data)
        : await api.post<Campagne>(`/stores/${storeId}/campaigns`, parsed.data)
      onEnregistre(enregistree)
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Enregistrement impossible.')
    } finally {
      setOccupe(false)
    }
  }

  return (
    <Dialog open fullWidth maxWidth="lg" onClose={onFermer}>
      <DialogTitle>{campagne ? 'Modifier la campagne' : 'Nouvelle campagne'}</DialogTitle>

      <DialogContent dividers>
        {erreur && (
          <Alert severity="error" sx={{ mb: 4 }} onClose={() => setErreur(null)}>
            {erreur}
          </Alert>
        )}

        {campagne && !modifiable && (
          <Alert
            severity="info"
            sx={{ mb: 4 }}
            action={
              onRapport && (
                <Button size="small" onClick={() => onRapport(campagne.id)}>
                  Voir le rapport
                </Button>
              )
            }
          >
            Cette campagne est <strong>{LIBELLES_STATUT_CAMPAGNE[campagne.status].toLowerCase()}</strong> :
            son contenu ne se modifie plus. Les commandes de pilotage sont au pied de la fenêtre.
          </Alert>
        )}

        <Box
          sx={{
            display: 'grid',
            gap: 5,
            gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.4fr) minmax(0, 1fr)' },
          }}
        >
          {/* ------------------------------------------------ colonne gauche */}
          {/* ⭐ `fieldset` désactivé : en HTML, il neutralise TOUS les champs
              qu'il contient, y compris ceux des composants enfants. Poser
              `disabled` sur quinze champs un à un aurait laissé passer le
              seizième au premier ajout. */}
          <Box
            component="fieldset"
            disabled={!modifiable}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 4,
              border: 0,
              p: 0,
              m: 0,
              minWidth: 0,
            }}
          >
            <TextField
              label="Nom de la campagne"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              size="small"
              autoFocus
              required
            />
            <TextField
              label="Objet interne"
              value={objet}
              onChange={(e) => setObjet(e.target.value)}
              size="small"
              helperText="Pour vous seul : il ne part jamais au client."
            />

            <Divider textAlign="left">
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Message
              </Typography>
            </Divider>

            <TextField
              label="Message envoyé"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              multiline
              minRows={6}
              size="small"
              inputProps={{ maxLength: 1200 }}
              helperText={`${message.length} / 1024 caractères`}
            />

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Insérer :
              </Typography>
              {TOKENS_MESSAGE.map((t) => (
                <Chip
                  key={t}
                  label={t}
                  size="small"
                  onClick={modifiable ? () => insererToken(t) : undefined}
                />
              ))}
            </Box>

            {/* Le contrôle tourne pendant la frappe : découvrir au moment de
                l'envoi qu'un message est trop long est trop tard. */}
            {defauts.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {defauts.map((d, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    {d.gravite === 'bloquant' ? (
                      <ErrorIcon sx={{ fontSize: 16, color: 'error.main', mt: '2px' }} />
                    ) : d.gravite === 'avertissement' ? (
                      <WarningIcon sx={{ fontSize: 16, color: 'warning.main', mt: '2px' }} />
                    ) : (
                      <InfoIcon sx={{ fontSize: 16, color: 'text.disabled', mt: '2px' }} />
                    )}
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {d.message}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            {defauts.length === 0 && message.length > 0 && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Message conforme.
                </Typography>
              </Box>
            )}

            <Divider textAlign="left">
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Visuel
              </Typography>
            </Divider>

            {/* ⭐ Deux façons d'illustrer, parce qu'il y a deux situations : la
                photo qu'on a sous la main, et l'affiche déjà en ligne. Exiger
                un dépôt obligerait à retélécharger ce qu'on possède déjà. */}
            <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              {modifiable && (
                <ChoixPortrait
                  value={mediaUrl.startsWith('data:') ? mediaUrl : null}
                  onChange={(img) => setMediaUrl(img ?? '')}
                  forme="carree"
                  taille={72}
                  compact
                />
              )}
              <TextField
                size="small"
                label="…ou lien vers une image"
                placeholder="https://…/affiche.jpg"
                value={mediaUrl.startsWith('data:') ? '' : mediaUrl}
                onChange={(e) => setMediaUrl(e.target.value)}
                disabled={mediaUrl.startsWith('data:')}
                sx={{ flex: 1, minWidth: 240 }}
                helperText={
                  mediaUrl.startsWith('data:')
                    ? 'Image déposée. Retirez-la pour saisir un lien à la place.'
                    : 'WhatsApp va chercher l’image à cette adresse : elle doit être publique.'
                }
              />
            </Box>

            {mediaUrl && !mediaUrl.startsWith('data:') && (
              <Box
                component="img"
                src={mediaUrl}
                alt="Aperçu du visuel"
                sx={{ maxHeight: 140, maxWidth: '100%', borderRadius: 2, objectFit: 'contain' }}
                onError={(e: { currentTarget: HTMLImageElement }) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
            )}

            <Divider textAlign="left">
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Destinataires
              </Typography>
            </Divider>

            <TextField
              select
              size="small"
              label="Segment de clients"
              value={segment.type}
              onChange={(e) => changerType(e.target.value as TypeSegment)}
              helperText={DESCRIPTIONS_SEGMENT[segment.type]}
            >
              {TYPES_SEGMENT.map((t) => (
                <MenuItem key={t} value={t}>
                  {LIBELLES_SEGMENT[t]}
                </MenuItem>
              ))}
            </TextField>

            {segment.type === 'active' && (
              <TextField
                size="small"
                type="number"
                label="Venus depuis moins de (jours)"
                value={segment.joursActivite}
                onChange={(e) =>
                  setSegment({ type: 'active', joursActivite: Number(e.target.value) || 1 })
                }
              />
            )}

            {segment.type === 'inactive' && (
              <TextField
                size="small"
                type="number"
                label="Sans passage depuis (jours)"
                value={segment.joursInactivite}
                onChange={(e) =>
                  setSegment({ type: 'inactive', joursInactivite: Number(e.target.value) || 1 })
                }
                helperText="Les fiches créées récemment sont exclues : on ne relance pas une cliente inscrite la semaine dernière."
              />
            )}

            {segment.type === 'vip' && (
              <Box sx={{ display: 'flex', gap: 3 }}>
                <TextField
                  size="small"
                  label="Étiquette"
                  value={segment.etiquette}
                  onChange={(e) =>
                    setSegment({ ...segment, etiquette: e.target.value || 'VIP' })
                  }
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Ou dépense supérieure à"
                  value={segment.seuilDepense ?? ''}
                  onChange={(e) =>
                    setSegment({
                      ...segment,
                      seuilDepense: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                  sx={{ flex: 1 }}
                />
              </Box>
            )}

            {segment.type === 'custom' && (
              <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: '1fr 1fr' }}>
                <TextField
                  size="small"
                  label="Étiquettes (séparées par une virgule)"
                  value={segment.etiquettes?.join(', ') ?? ''}
                  onChange={(e) =>
                    setSegment({
                      ...segment,
                      etiquettes: e.target.value
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                  sx={{ gridColumn: '1 / -1' }}
                />
                <TextField
                  size="small"
                  label="Ville"
                  value={segment.ville ?? ''}
                  onChange={(e) => setSegment({ ...segment, ville: e.target.value || undefined })}
                />
                <TextField
                  size="small"
                  type="number"
                  label="Dépense minimale"
                  value={segment.depenseMin ?? ''}
                  onChange={(e) =>
                    setSegment({
                      ...segment,
                      depenseMin: e.target.value ? Number(e.target.value) : undefined,
                    })
                  }
                />
                <ChampDate
                  label="Dernière visite avant le"
                  value={segment.derniereVisiteAvant ?? ''}
                  onChange={(v) =>
                    setSegment({ ...segment, derniereVisiteAvant: v || undefined })
                  }
                />
                <ChampDate
                  label="Inscrits après le"
                  value={segment.creeApres ?? ''}
                  onChange={(v) => setSegment({ ...segment, creeApres: v || undefined })}
                />
              </Box>
            )}

            {/* ⭐ Le segment PRÉ-SÉLECTIONNE, la liste TRANCHE. Seuls les
                clients cochés reçoivent le message. */}
            <ChoixDestinataires
              storeId={storeId}
              segment={segment}
              selection={selection}
              onChange={setSelection}
            />

            <Divider textAlign="left">
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Envoi
              </Typography>
            </Divider>

            <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <ChampDate
                  label="Date d’envoi"
                  value={jour}
                  onChange={setJour}
                  min={aujourdhui()}
                  helperText="Vide = la campagne reste en brouillon et se lance à la main."
                />
              </Box>
              <TextField
                select
                size="small"
                label="Heure"
                value={heure}
                onChange={(e) => setHeure(e.target.value)}
                sx={{ minWidth: 110 }}
                disabled={!jour}
              >
                {HEURES.map((h) => (
                  <MenuItem key={h} value={h}>
                    {h}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                Cadence : {options.cadenceParMinute} messages par minute
              </Typography>
              <Slider
                value={options.cadenceParMinute}
                min={1}
                max={60}
                step={1}
                onChange={(_, v) =>
                  setOptions({ ...options, cadenceParMinute: Array.isArray(v) ? v[0]! : v })
                }
              />
              {/* ⚠️ Ce n'est pas un réglage de confort : au-delà d'une certaine
                  cadence, un numéro expéditeur récent est traité comme un
                  émetteur de masse et se fait limiter par l'opérateur. */}
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                Une cadence élevée fait repérer votre numéro comme émetteur de masse.
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', gap: 3 }}>
              <TextField
                select
                size="small"
                label="Pas avant"
                value={options.heureDebut}
                onChange={(e) => setOptions({ ...options, heureDebut: Number(e.target.value) })}
                sx={{ flex: 1 }}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <MenuItem key={h} value={h}>
                    {h} h
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Pas après"
                value={options.heureFin}
                onChange={(e) => setOptions({ ...options, heureFin: Number(e.target.value) })}
                sx={{ flex: 1 }}
              >
                {Array.from({ length: 24 }, (_, h) => h + 1).map((h) => (
                  <MenuItem key={h} value={h}>
                    {h} h
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Suspendre au-delà de"
                value={options.seuilEchecPct}
                onChange={(e) => setOptions({ ...options, seuilEchecPct: Number(e.target.value) })}
                sx={{ flex: 1 }}
              >
                {[10, 15, 25, 40, 60].map((p) => (
                  <MenuItem key={p} value={p}>
                    {p} % d’échecs
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>

          {/* ------------------------------------------------- colonne droite */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {/* ⭐ L'estimation EN HAUT, avant l'assistant : c'est le chiffre qui
                fait changer le message, pas l'inverse. */}
            <Paper variant="outlined" sx={{ p: 4, borderRadius: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <GroupIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Destinataires
                </Typography>
              </Box>

              {estimationEnCours && !estimation ? (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Calcul en cours…
                </Typography>
              ) : estimation ? (
                <>
                  <Typography variant="h2" sx={{ lineHeight: 1.1 }}>
                    {estimation.joignables}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
                    joignables sur {estimation.clients} client
                    {estimation.clients > 1 ? 's' : ''} retenus
                  </Typography>

                  {/* ⭐ Les écartés sont DITS. Un fichier de 800 clients dont
                      300 sans mobile n'est pas une campagne de 800 messages, et
                      mieux vaut l'apprendre ici que dans le rapport. */}
                  {estimation.ecartes > 0 && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                      {estimation.ecartes} fiche{estimation.ecartes > 1 ? 's' : ''} écartée
                      {estimation.ecartes > 1 ? 's' : ''} : numéro absent, illisible ou en double.
                    </Alert>
                  )}

                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Durée d’envoi estimée :{' '}
                    <strong>
                      {estimation.dureeMinutes < 1 ? 'moins d’une' : estimation.dureeMinutes} minute
                      {estimation.dureeMinutes > 1 ? 's' : ''}
                    </strong>
                  </Typography>

                  {estimation.apercu.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Aperçu
                      </Typography>
                      {estimation.apercu.map((a) => (
                        <Typography key={a.phone} variant="caption" component="div">
                          {a.nom} · {a.phone}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Estimation indisponible. La liste sera figée à la préparation.
                </Typography>
              )}
            </Paper>

            {/* Un conseiller n'a rien à proposer sur un texte qu'on ne peut
                plus toucher : sur une campagne partie, c'est le rapport qui
                porte l'analyse. */}
            {modifiable && (
              <AssistantIA
                storeId={storeId}
                message={message}
                segment={segment}
                onUtiliser={setMessage}
              />
            )}
          </Box>
        </Box>
      </DialogContent>

      {/**
        * Le pied de la fenêtre porte la GESTION, pas seulement l'enregistrement.
        *
        * ⭐ C'est ce qui manquait : la ligne du tableau ouvrait le rapport, si
        * bien qu'une campagne suspendue ne se reprenait que par un menu
        * contextuel qu'il fallait connaître. Ici, ce qu'on peut faire est sous
        * les yeux — et grisé quand l'état ne le permet pas.
        */}
      <DialogActions sx={{ px: 6, py: 4, gap: 2, flexWrap: 'wrap' }}>
        {campagne && onRapport && (
          <Button onClick={() => onRapport(campagne.id)}>Rapport</Button>
        )}

        {/* ⭐ Rejouer CRÉE une copie en brouillon. Remettre celle-ci à zéro
            aurait effacé ses compteurs et son journal — c'est-à-dire ce à quoi
            on veut comparer le second envoi. */}
        {campagne && onRejouer && (
          <Button onClick={() => onRejouer(campagne)}>Rejouer</Button>
        )}

        <Box sx={{ flex: 1 }} />

        {campagne && onAction && transitionPossible(campagne.status, 'paused') && (
          <Button
            color="warning"
            onClick={() => onAction(campagne, 'suspension', 'Campagne suspendue')}
          >
            Suspendre
          </Button>
        )}

        {campagne && onAction && campagne.status === 'running' && (
          <Button onClick={() => onAction(campagne, 'avancement', 'Lot traité')}>
            Envoyer un lot
          </Button>
        )}

        {campagne && onAction && transitionPossible(campagne.status, 'running') && (
          <Button
            variant="contained"
            onClick={() =>
              campagne.status === 'paused'
                ? onAction(campagne, 'reprise', 'Envoi repris')
                : onAction(campagne, 'lancement', 'Campagne lancée')
            }
          >
            {campagne.status === 'paused' ? 'Reprendre' : 'Lancer'}
          </Button>
        )}

        <Button onClick={onFermer}>{modifiable ? 'Annuler' : 'Fermer'}</Button>

        {modifiable && (
          <Button
            variant="contained"
            disabled={occupe || bloquants.length > 0}
            onClick={() => void enregistrer()}
          >
            {jour ? 'Programmer' : 'Enregistrer le brouillon'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
